import { useContext, useEffect, useRef } from "react";
import BeelditTableHeaderActions from "@beeldit/core/components/BeelditTableHeaderActions";
import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";
import { t } from "i18next";

import { Card } from "react-bootstrap";
import useListAction from "@beeldit/core/actions/list/useListAction";
import useRemoveAction from "@beeldit/core/actions/remove/useRemoveAction";
import { getTableConfig } from "@beeldit/core/helpers/tables";
import usePayAction from "../actions/pay/usePayAction";
import useGenerateDeliveryNoteAction from "../actions/generate-delivery-note/useGenerateDeliveryNoteAction";
import useGenerateInvoiceAction from "../actions/generate-invoice/useGenerateInvoiceAction";
import useCreateAction from "../actions/create/useCreateAction";
import { useLocation, useNavigate } from "react-router-dom";
import useCancelAction from "../actions/cancel/useCancelAction";
import useUncancelAction from "../actions/uncancel/useUncancelAction";
import useEditAction from "../actions/edit/useEditAction";
import useUnpayAction from "../actions/unpay/useUnpayAction";
import useEmailAction from "../actions/email/useEmailAction";
import useGenerateInvoicePdfAction from "../actions/generate-invoice-pdf/useGenerateInvoicePdfAction";
import useRegenerateInvoicePdfAction from "../actions/regenerate-invoice-pdf/useRegenerateInvoicePdfAction";
import useDownloadPDFAction from "@main/modules/bloonde/billing/invoices/actions/download-pdf/DownloadPDFAction";
import useEditInvoiceAction from "@main/modules/bloonde/billing/invoices/actions/edit/useEditAction";
import { EditActionProvider } from "@main/modules/bloonde/billing/invoices/actions/edit/EditActionContext";
import InvoiceAccountSelector from "@main/modules/bloonde/billing/invoice-accounts/components/InvoiceAccountSelector";
import useGenerateDeliveryNotePdfAction from "../actions/generate-delivery-note-pdf/useGenerateDeliveryNotePdfAction";
import useDownloadDeliveryNotePDFAction from "@main/modules/bloonde/billing/delivery-notes/actions/download-pdf/DownloadPDFAction";
import useEditDeliveryNoteAction from "@main/modules/bloonde/billing/delivery-notes/actions/edit/useEditAction";
import { EditActionProvider as DeliveryNoteEditActionProvider } from "@main/modules/bloonde/billing/delivery-notes/actions/edit/EditActionContext";
import { GenerateInvoicePdfActionProvider } from "../actions/generate-invoice-pdf/GenerateInvoicePdfActionContext";
import { GenerateDeliveryNotePdfActionProvider } from "../actions/generate-delivery-note-pdf/GenerateDeliveryNotePdfActionContext";
import { RegenerateInvoicePdfActionProvider } from "../actions/regenerate-invoice-pdf/RegenerateInvoicePdfActionContext";
import replicateInvoice from "@main/modules/bloonde/billing/invoices/actions/replicate/replicateInvoice";

function BookingsTableInner(props: { modelConfig: any; templates?: [] }) {
  const { modelConfig } = props;
  const { user } = useContext(AuthContext);

  const {
    indexFunction,
    actionTemplate: listActionTemplate,
    setTableConfig,
  } = useListAction(modelConfig, user);

  const { initializeFunction, actionTemplate: createActionTemplate } =
    useCreateAction(modelConfig, indexFunction);

  const { prepareRemoveFunction, actionTemplate: removeActionTemplate } =
    useRemoveAction(modelConfig, indexFunction);

  const { preparePayFunction, actionTemplate: payActionTemplate } =
    usePayAction(modelConfig, indexFunction);

  const { prepareUnpayFunction, actionTemplate: unpayActionTemplate } =
    useUnpayAction(modelConfig, indexFunction);

  const { prepareCancelFunction, actionTemplate: cancelActionTemplate } =
    useCancelAction(modelConfig, indexFunction);

  const { prepareUncancelFunction, actionTemplate: uncancelActionTemplate } =
    useUncancelAction(modelConfig, indexFunction);
  const {
    prepareGenerateDeliveryNoteFunction,
    actionTemplate: generateDeliveryNoteActionTemplate,
  } = useGenerateDeliveryNoteAction(modelConfig, indexFunction);

  const {
    prepareGenerateInvoiceFunction,
    actionTemplate: generateInvoiceActionTemplate,
  } = useGenerateInvoiceAction(modelConfig, indexFunction);

  const { getFunction, actionTemplate: editActionTemplate } = useEditAction(
    modelConfig,
    indexFunction,
    prepareCancelFunction,
    prepareUncancelFunction
  );

  const { prepareEmailFunction, actionTemplate: emailActionTemplate } =
      useEmailAction(modelConfig, indexFunction);
  const {
    prepareGenerateInvoicePdfFunction,
    actionTemplate: generateInvoicePdfActionTemplate,
  } = useGenerateInvoicePdfAction(modelConfig, indexFunction);
  const {
    prepareRegenerateInvoicePdfFunction,
    actionTemplate: regenerateInvoicePdfActionTemplate,
  } = useRegenerateInvoicePdfAction(modelConfig, indexFunction);
  const { downloadPDFFunction } = useDownloadPDFAction();
  const { getFunction: getInvoiceFunction, actionTemplate: editInvoiceActionTemplate } = useEditInvoiceAction(
    indexFunction,
    {},
    { receiver_accountable_id: InvoiceAccountSelector },
    {},
    true
  );

  const {
    prepareGenerateDeliveryNotePdfFunction,
    actionTemplate: generateDeliveryNotePdfActionTemplate,
  } = useGenerateDeliveryNotePdfAction(modelConfig, indexFunction);
  const { downloadPDFFunction: downloadDeliveryNotePDFFunction } = useDownloadDeliveryNotePDFAction();
  const { getFunction: getDeliveryNoteFunction, actionTemplate: editDeliveryNoteActionTemplate } = useEditDeliveryNoteAction(
    indexFunction,
    {},
    { receiver_accountable_id: InvoiceAccountSelector },
    {},
    true
  );

  /**<newActionsHooks**/

  /**
   * Table configuration definition
   */
  let tableConfig = getTableConfig(modelConfig.tableConfig, {
    indexFunction: indexFunction,
    actionsFunctions: [
      {
        name: "create",
        function: initializeFunction,
      },
    ],
    bulkFunctions: [
      {
        name: "create-delivery-note",
        function: prepareGenerateDeliveryNoteFunction,
      },
      {
        name: "create-invoice",
        function: prepareGenerateInvoiceFunction,
      },
      /**<newBulkActions**/
    ],
    rowFunctions: [
      {
        name: "edit",
        function: getFunction,
      },
      {
        name: "delete",
        function: prepareRemoveFunction,
      },
      {
        name: "pay",
        function: preparePayFunction,
      },
      {
        name: "unpay",
        function: prepareUnpayFunction,
      },
      {
        name: "email",
        function: prepareEmailFunction,
      },
      {
        name: "cancel",
        function: prepareCancelFunction,
      },
      {
        name: "uncancel",
        function: prepareUncancelFunction,
      },
      {
        name: "generate-invoice-pdf",
        function: (invoiceId: any) => prepareGenerateInvoicePdfFunction(invoiceId),
      },
      {
        name: "regenerate-invoice-pdf",
        function: (invoiceId: any) => prepareRegenerateInvoicePdfFunction(invoiceId),
      },
      {
        name: "download-invoice-pdf",
        function: (url: any) => downloadPDFFunction(url),
      },
      {
        name: "edit-invoice",
        function: (invoiceId: any) => getInvoiceFunction(invoiceId),
      },
      {
        name: "replicate-invoice",
        function: (invoiceId: any) => {
          replicateInvoice(invoiceId, (newInvoiceId: number) => {
            getInvoiceFunction(newInvoiceId);
          });
        },
      },
      {
        name: "generate-delivery-note-pdf",
        function: (deliveryNoteId: any) => prepareGenerateDeliveryNotePdfFunction(deliveryNoteId),
      },
      {
        name: "download-delivery-note-pdf",
        function: (url: any) => downloadDeliveryNotePDFFunction(url),
      },
      {
        name: "edit-delivery-note",
        function: (deliveryNoteId: any) => getDeliveryNoteFunction(deliveryNoteId),
      },
      {
        name: "replicate-delivery-note",
        function: (deliveryNoteId: any) => {
          replicateInvoice(deliveryNoteId, (newDeliveryNoteId: number) => {
            getDeliveryNoteFunction(newDeliveryNoteId);
          });
        },
      },
    ],
  });
  setTableConfig(tableConfig);

  const location = useLocation(); // Obtener la ubicación de la URL
  const navigate = useNavigate();
  const hasOpenedFromQuery = useRef(false); // Evita abrir múltiples veces

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const isNew = urlParams.get("new");
    const customerId = urlParams.get("customer_id");

    if ((isNew === "true" || customerId) && !hasOpenedFromQuery.current) {
      hasOpenedFromQuery.current = true;
      
      // Si hay customer_id, inicializar con ese cliente preseleccionado
      if (customerId) {
        initializeFunction({ customer_id: parseInt(customerId) });
      } else {
        initializeFunction();
      }

      // Eliminar los parámetros de la URL para que no se vuelva a abrir al cerrar
      if (isNew) urlParams.delete("new");
      if (customerId) urlParams.delete("customer_id");
      const newSearch = urlParams.toString();
      const newPath = location.pathname + (newSearch ? `?${newSearch}` : "");
      navigate(newPath, { replace: true });
    }
  }, [location.pathname, location.search, navigate, initializeFunction]);

  return (
    <Card>
      <Card.Header className="d-flex align-items-center justify-content-between ps-3 bg-white">
        <span>{t(modelConfig.modelPluralName)}</span>
        <div>
          <BeelditTableHeaderActions
            actions={tableConfig.actions}
            bulkActions={tableConfig.bulkActions}
          ></BeelditTableHeaderActions>
        </div>
      </Card.Header>

      <Card.Body>
        {listActionTemplate}
        {createActionTemplate}
        {editActionTemplate}
        {removeActionTemplate}
        {payActionTemplate}
        {unpayActionTemplate}
        {cancelActionTemplate}
        {uncancelActionTemplate}
  {emailActionTemplate}
  {generateDeliveryNoteActionTemplate}
  {generateInvoiceActionTemplate}
  {generateInvoicePdfActionTemplate}
  {regenerateInvoicePdfActionTemplate}
  {editInvoiceActionTemplate}
  {generateDeliveryNotePdfActionTemplate}
  {editDeliveryNoteActionTemplate}
        {/* newTemplateActions */}
      </Card.Body>
    </Card>
  );
}

export default function BookingsTablePage(props: { modelConfig: any; templates?: [] }) {
  return (
    <GenerateInvoicePdfActionProvider>
      <RegenerateInvoicePdfActionProvider>
        <GenerateDeliveryNotePdfActionProvider>
          <EditActionProvider>
            <DeliveryNoteEditActionProvider>
              <BookingsTableInner {...props} />
            </DeliveryNoteEditActionProvider>
          </EditActionProvider>
        </GenerateDeliveryNotePdfActionProvider>
      </RegenerateInvoicePdfActionProvider>
    </GenerateInvoicePdfActionProvider>
  );
}
